import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import LockedOutPay from '../components/LockedOutPay';
import { ErrorBox, Field } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import api, { errorDetail, errorMessage, LOCKED_REASON_KEY } from '../services/api';
import { getDeviceId } from '../utils/device';

/** Shop staff and the Super Admin sign in on different pages; these tabs switch between them. */
export function LoginTabs({ active }: { active: 'shop' | 'admin' }) {
  const tab = (key: 'shop' | 'admin', to: string, label: string) => (
    <Link to={to} aria-current={active === key ? 'page' : undefined}
      className={`flex-1 rounded-md px-3 py-1.5 text-center text-sm font-medium ${active === key
        ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{label}</Link>
  );
  return (
    <div className="mb-5 flex gap-1 rounded-lg bg-slate-100 p-1">
      {tab('shop', '/login', 'Shop')}
      {tab('admin', '/admin/login', 'Super Admin')}
    </div>
  );
}

export function homeFor(role: string, hasCounter: boolean) {
  if (role === 'cashier') return '/billing';
  return hasCounter ? '/billing' : '/dashboard';
}

export default function Login() {
  const { login, session, refresh, setToken } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(localStorage.getItem(LOCKED_REASON_KEY) || '');
  const [busy, setBusy] = useState(false);
  const [canPay, setCanPay] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [signupOn, setSignupOn] = useState(false);

  useEffect(() => {
    localStorage.removeItem(LOCKED_REASON_KEY);
    api.get('/public/config').then(({ data }) => setSignupOn(data.self_signup_enabled)).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (session && !session.must_change_password && !mustChange) navigate(homeFor(session.user.role, !!session.counter), { replace: true });
  }, [session, mustChange, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(''); setCanPay(false);
    try {
      const s = await login(username, password);
      if (s.must_change_password) setMustChange(true);
    } catch (err) {
      const d = errorDetail<{ code?: string; can_pay?: boolean }>(err);
      setError(errorMessage(err));
      if (d?.code === 'subscription_locked') setCanPay(!!d.can_pay);
    } finally { setBusy(false); }
  };

  const changePw = async (e: FormEvent) => {
    e.preventDefault();
    if (pw1 !== pw2) { setError('The two passwords do not match'); return; }
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/auth/change-password', { current_password: password, new_password: pw1 });
      setToken(data.access_token);
      setMustChange(false);
      await refresh();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-brand-800 to-slate-900 p-4">
      <div className="card w-full max-w-sm p-6">
        <LoginTabs active="shop" />
        <h1 className="text-2xl font-bold text-brand-800">Zentrix POS</h1>
        <p className="mb-5 text-sm text-slate-500">Billing &amp; inventory</p>
        <ErrorBox>{error}</ErrorBox>
        {canPay && <button className="btn-primary mb-3 w-full" onClick={() => setPayOpen(true)}>Pay subscription now</button>}
        {!mustChange ? (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Username or mobile number"><input className="input" value={username} autoFocus
              onChange={(e) => setUsername(e.target.value)} required autoComplete="username" /></Field>
            <Field label="Password"><input className="input" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></Field>
            <button className="btn-primary btn-big w-full" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          </form>
        ) : (
          <form onSubmit={changePw} className="space-y-3">
            <p className="text-sm">Please choose a new password before continuing.</p>
            <Field label="New password" hint="At least 8 characters"><input className="input" type="password" value={pw1} minLength={8}
              onChange={(e) => setPw1(e.target.value)} required autoFocus autoComplete="new-password" /></Field>
            <Field label="Repeat new password"><input className="input" type="password" value={pw2}
              onChange={(e) => setPw2(e.target.value)} required autoComplete="new-password" /></Field>
            <button className="btn-primary w-full" disabled={busy}>Save and continue</button>
          </form>
        )}
        <div className="mt-5 flex justify-between text-xs text-slate-400">
          <span title={getDeviceId()}>Device {getDeviceId().slice(0, 8)}</span>
          {signupOn && <Link to="/signup" className="hover:text-brand-700">Open a shop</Link>}
        </div>
      </div>
      <LockedOutPay open={payOpen} onClose={() => setPayOpen(false)} creds={{ username, password }} />
    </div>
  );
}
